// The client-side world model: what the game knows, independent of how it
// is drawn. Nothing in core/ imports Three.js — that is the common-core
// layering rule (see CLAUDE.md).

import {
  type AreaPlan,
  type ArtifactUsedMessage,
  asCarriage,
  asCreaturePosture,
  asCreatureSpecies,
  asPowerFocus,
  asQuarter,
  asWardenBand,
  asWardenState,
  type BoltMessage,
  type Carriage,
  CHAT_LOG_LIMIT,
  type ChatBroadcastMessage,
  type ClubState,
  type CreaturePosture,
  type CreatureSpecies,
  type FlyerTakenMessage,
  type HomeState,
  homeGrid,
  INTERP_DELAY_MS,
  isHomeRoomId,
  isPlaceableKind,
  type JobResultMessage,
  lerpAngle,
  type MarketResultMessage,
  type PlayerPose,
  type PowerFocus,
  parseHomeRoomId,
  placeableAabb,
  placeableObstacles,
  type Quarter,
  resolvePlan,
  type SampleResultMessage,
  type ShipPose,
  type SkiffPose,
  SnapshotBuffer,
  type SpaceState,
  type StructureKind,
  type SurfaceState,
  type SurveyResultMessage,
  structureFootprint,
  type ThreatMessage,
  TRADE_RANGE_M,
  type TradeRequestMessage,
  type TradeResultMessage,
  type WardenBand,
  type WardenStateWord,
} from "@bebop/shared";

export type ConnectionStatus = "connecting" | "connected" | "reconnecting" | "failed";

export interface RemotePlayer {
  name: string;
  /** Appearance JSON as replicated (parse with parseAppearance). */
  appearance: string;
  buffer: SnapshotBuffer;
  /** Pose sampled for the current frame (interpolated, in the past). */
  renderPose: PlayerPose;
  /** Skiff id while riding, else "". */
  mounted: string;
  /** How this body is carrying itself — a word, never a number. */
  carriage: Carriage;
}

export interface SelfPlayer {
  name: string;
  /** Appearance JSON as replicated (parse with parseAppearance). */
  appearance: string;
  /** Locally predicted pose — what the camera and own avatar follow. */
  pose: PlayerPose;
  /** Last authoritative pose received from the server. */
  serverPose: PlayerPose;
  /** Skiff id while riding, else "". */
  mounted: string;
  /**
   * How this body is carrying itself. Fed straight back into local
   * prediction so a capped gait is predicted rather than corrected —
   * a server-only slow the client could not reproduce would snap the
   * avatar several times a second, on exactly the player already having
   * the worst time.
   */
  carriage: Carriage;
}

export interface RemoteSkiff {
  buffer: SnapshotBuffer;
  /** Pose sampled for the current frame (interpolated, in the past). */
  renderPose: PlayerPose;
  /** Latest authoritative pose (seeds prediction on mount). */
  server: SkiffPose;
  /** Latest authoritative speed, m/s — drives bob/dust intensity. */
  speed: number;
  riderId: string;
  /** Passenger sessionId, "" while the second seat is empty. */
  passengerId: string;
  /** The plotted run's placard — a destination label, or "". */
  plotted: string;
  /** Band WORDS — the only wear/charge state that ever reaches a client. */
  wearBand: string;
  chargeBand: string;
  /** Owner's display name for prompts, "" while unowned. */
  ownerName: string;
  locked: boolean;
  town: boolean;
}

/** The skiff YOU are riding: locally predicted like your own body. */
export interface SelfSkiff {
  id: string;
  pose: SkiffPose;
  serverPose: SkiffPose;
}

/** A ship as seen from outside: interpolated, with altitude. */
export interface RemoteShip {
  buffer: SnapshotBuffer;
  /** Pose sampled for the current frame (interpolated, in the past). */
  renderPose: PlayerPose & { y: number };
  /** Latest authoritative pose (seeds prediction on mount). */
  server: ShipPose;
  speed: number;
  vy: number;
  riderId: string;
  /** Passenger sessionId, "" while the second seat is empty. */
  passengerId: string;
  /** The plotted run's placard — a destination label, or "". */
  plotted: string;
  wearBand: string;
  chargeBand: string;
  ownerName: string;
  locked: boolean;
  /** The manifold, replicated: routing word + the three meters. */
  focus: PowerFocus;
  boost: number;
  coil: number;
  veil: number;
  boosting: boolean;
  /** Combat state: the trip and how close it is. */
  adrift: boolean;
  strain: number;
}

/** The ship YOU are flying: locally predicted like your own body. */
export interface SelfShip {
  id: string;
  pose: ShipPose;
  serverPose: ShipPose;
}

/** A warden as seen from a cockpit: interpolated, worded, never numbered. */
export interface RemoteWarden {
  buffer: SnapshotBuffer;
  renderPose: PlayerPose & { y: number };
  state: WardenStateWord;
  band: WardenBand;
}

/** A homestead stake within interest range. Words and a place. */
export interface RemoteClaim {
  name: string;
  ownerName: string;
  x: number;
  z: number;
}

/** A raised structure within interest range. */
export interface RemoteStructure {
  kind: StructureKind;
  x: number;
  z: number;
  quarter: Quarter;
  claimId: string;
  claimName: string;
  ownerName: string;
  /** Door policy word: "open" | "shut" | "invited". */
  door: string;
}

/** A piece of furniture in the home room you are standing in. */
export interface RemotePlaceable {
  kind: string;
  cellX: number;
  cellZ: number;
  rot: Quarter;
  carried: boolean;
  makerName: string;
}

/**
 * A creature as this client sees it: an interpolated pose, a species word
 * and a posture word. That is all the server sends and all the renderer
 * needs — no vigor, no health, no target, no number.
 */
export interface RemoteCreature {
  species: CreatureSpecies;
  buffer: SnapshotBuffer;
  /** Pose sampled for the current frame (interpolated, in the past). */
  renderPose: PlayerPose;
  posture: CreaturePosture;
  /** Latest authoritative ground speed, m/s — drives the gait animation. */
  speed: number;
}

/** Snap skiff prediction to the server when it diverges this far. */
const SKIFF_SNAP_DISTANCE = 4;
const SKIFF_RECONCILE_RATE = 0.25;

/** Ship prediction reconciliation (3D — altitude reconciles too). */
const SHIP_SNAP_DISTANCE = 10;
const SHIP_RECONCILE_RATE = 0.25;

export interface ChatEntry extends ChatBroadcastMessage {
  id: number;
}

export interface MusicState {
  live: boolean;
  nowPlaying: string;
  listeners: number;
}

/** Snap prediction to the server when it diverges this far (metres). */
const SNAP_DISTANCE = 1.5;
/** Per-patch blend factor pulling prediction toward the server pose. */
const RECONCILE_RATE = 0.2;

export class World {
  status: ConnectionStatus = "connecting";
  /** Which area we're connected to (an AreaPlan id); empty until joined. */
  roomId = "";
  selfId: string | undefined;
  self: SelfPlayer | undefined;
  selfSkiff: SelfSkiff | undefined;
  selfShip: SelfShip | undefined;
  readonly remotes = new Map<string, RemotePlayer>();
  readonly skiffs = new Map<string, RemoteSkiff>();
  readonly ships = new Map<string, RemoteShip>();
  readonly wardens = new Map<string, RemoteWarden>();
  readonly creatures = new Map<string, RemoteCreature>();
  /** Stakes and shells within interest range (surface rooms only). */
  readonly claims = new Map<string, RemoteClaim>();
  readonly structures = new Map<string, RemoteStructure>();
  /** Furniture in the home room you are standing in (home rooms only). */
  readonly placeables = new Map<string, RemotePlaceable>();
  /** The home room's own words (home rooms only). */
  homeLabel = "";
  homeOwnerName = "";
  homeDoor = "";
  readonly chat: ChatEntry[] = [];
  readonly music: MusicState = { live: false, nowPlaying: "", listeners: 0 };
  /**
   * Movement plan for prediction: the room's plan with player-raised
   * obstacles folded in — structure shells on the surface, solid furniture
   * indoors — rebuilt whenever the replicated rows change. The server
   * integrates against the same lists, so nothing rubber-bands.
   */
  private predictionPlan: AreaPlan | undefined;

  onChat: ((entry: ChatEntry) => void) | undefined;
  onSurveyResult: ((result: SurveyResultMessage) => void) | undefined;
  onSampleResult: ((result: SampleResultMessage) => void) | undefined;
  onTradeRequest: ((request: TradeRequestMessage) => void) | undefined;
  onFlyerTaken: ((m: FlyerTakenMessage) => void) | undefined;
  onTradeResult: ((result: TradeResultMessage) => void) | undefined;
  /** A bolt streaked across somebody's sky — the render layer draws it. */
  onBolt: ((bolt: BoltMessage) => void) | undefined;
  onArtifactUsed: ((m: ArtifactUsedMessage) => void) | undefined;
  onThreat: ((m: ThreatMessage) => void) | undefined;
  onMarketResult: ((result: MarketResultMessage) => void) | undefined;
  onJobResult: ((result: JobResultMessage) => void) | undefined;

  private chatSeq = 0;

  /** Ingest an authoritative state patch. `now` is performance.now() ms. */
  applyState(state: ClubState | SurfaceState | SpaceState | HomeState, now: number): void {
    // The surface replicates no music; the pill simply stays dark out there.
    this.music.live = "live" in state ? state.live : false;
    this.music.nowPlaying = "nowPlaying" in state ? state.nowPlaying : "";
    this.music.listeners = "listeners" in state ? state.listeners : 0;

    this.applyHomesteads(state);

    const seen = new Set<string>();
    state.players.forEach((player, sessionId) => {
      seen.add(sessionId);
      if (sessionId === this.selfId) {
        this.applySelf(
          player.name,
          player.appearance,
          { x: player.x, z: player.z, yaw: player.yaw },
          player.mounted,
          asCarriage(player.carriage),
        );
      } else {
        this.applyRemote(
          sessionId,
          player.name,
          player.appearance,
          { x: player.x, z: player.z, yaw: player.yaw },
          player.mounted,
          asCarriage(player.carriage),
          now,
        );
      }
    });
    for (const id of this.remotes.keys()) {
      if (!seen.has(id)) this.remotes.delete(id);
    }

    this.applySkiffs("skiffs" in state ? state.skiffs : undefined, now);
    this.applyShips("ships" in state ? state.ships : undefined, now);
    this.applyWardens("wardens" in state ? state.wardens : undefined, now);
    this.applyCreatures("creatures" in state ? state.creatures : undefined, now);
  }

  private applyWardens(stateWardens: SpaceState["wardens"] | undefined, now: number): void {
    const seen = new Set<string>();
    stateWardens?.forEach((warden, id) => {
      seen.add(id);
      let entry = this.wardens.get(id);
      if (!entry) {
        entry = {
          buffer: new SnapshotBuffer(),
          renderPose: { x: warden.x, y: warden.y, z: warden.z, yaw: warden.yaw },
          state: asWardenState(warden.state),
          band: asWardenBand(warden.band),
        };
        this.wardens.set(id, entry);
      }
      entry.state = asWardenState(warden.state);
      entry.band = asWardenBand(warden.band);
      entry.buffer.push({ t: now, x: warden.x, y: warden.y, z: warden.z, yaw: warden.yaw });
    });
    for (const id of this.wardens.keys()) {
      if (!seen.has(id)) this.wardens.delete(id);
    }
  }

  private applyCreatures(stateCreatures: SurfaceState["creatures"] | undefined, now: number): void {
    const seen = new Set<string>();
    stateCreatures?.forEach((creature, id) => {
      seen.add(id);
      let entry = this.creatures.get(id);
      if (!entry) {
        entry = {
          species: asCreatureSpecies(creature.species),
          buffer: new SnapshotBuffer(),
          renderPose: { x: creature.x, z: creature.z, yaw: creature.yaw },
          posture: asCreaturePosture(creature.posture),
          speed: creature.speed,
        };
        this.creatures.set(id, entry);
      }
      entry.posture = asCreaturePosture(creature.posture);
      entry.speed = creature.speed;
      entry.buffer.push({ t: now, x: creature.x, z: creature.z, yaw: creature.yaw });
    });
    for (const id of this.creatures.keys()) {
      if (!seen.has(id)) this.creatures.delete(id);
    }
  }

  private applyShips(
    stateShips: SurfaceState["ships"] | SpaceState["ships"] | undefined,
    now: number,
  ): void {
    const seen = new Set<string>();
    stateShips?.forEach((ship, id) => {
      seen.add(id);
      const server: ShipPose = {
        x: ship.x,
        y: ship.y,
        z: ship.z,
        yaw: ship.yaw,
        speed: ship.speed,
        vy: ship.vy,
      };
      let entry = this.ships.get(id);
      if (!entry) {
        entry = {
          buffer: new SnapshotBuffer(),
          renderPose: { x: ship.x, y: ship.y, z: ship.z, yaw: ship.yaw },
          server,
          speed: ship.speed,
          vy: ship.vy,
          riderId: ship.riderId,
          passengerId: ship.passengerId,
          plotted: ship.plotted,
          wearBand: ship.wearBand,
          chargeBand: ship.chargeBand,
          ownerName: ship.ownerName,
          locked: ship.locked,
          focus: asPowerFocus(ship.focus),
          boost: ship.boost,
          coil: ship.coil,
          veil: ship.veil,
          boosting: ship.boosting,
          adrift: ship.adrift,
          strain: ship.strain,
        };
        this.ships.set(id, entry);
      }
      entry.server = server;
      entry.speed = ship.speed;
      entry.vy = ship.vy;
      entry.riderId = ship.riderId;
      entry.passengerId = ship.passengerId;
      entry.plotted = ship.plotted;
      entry.wearBand = ship.wearBand;
      entry.chargeBand = ship.chargeBand;
      entry.ownerName = ship.ownerName;
      entry.locked = ship.locked;
      entry.focus = asPowerFocus(ship.focus);
      entry.boost = ship.boost;
      entry.coil = ship.coil;
      entry.veil = ship.veil;
      entry.boosting = ship.boosting;
      entry.adrift = ship.adrift;
      entry.strain = ship.strain;
      entry.buffer.push({ t: now, x: ship.x, y: ship.y, z: ship.z, yaw: ship.yaw });

      // Reconcile the flown ship's prediction like the self pose — in 3D,
      // so a mispredicted climb pulls back gently instead of stair-stepping.
      const mine = this.selfShip;
      if (mine?.id === id) {
        mine.serverPose = server;
        const dx = server.x - mine.pose.x;
        const dy = server.y - mine.pose.y;
        const dz = server.z - mine.pose.z;
        if (Math.hypot(dx, dy, dz) > SHIP_SNAP_DISTANCE) {
          mine.pose = { ...server };
        } else {
          mine.pose.x += dx * SHIP_RECONCILE_RATE;
          mine.pose.y += dy * SHIP_RECONCILE_RATE;
          mine.pose.z += dz * SHIP_RECONCILE_RATE;
          mine.pose.yaw = lerpAngle(mine.pose.yaw, server.yaw, SHIP_RECONCILE_RATE);
          mine.pose.speed += (server.speed - mine.pose.speed) * SHIP_RECONCILE_RATE;
          mine.pose.vy += (server.vy - mine.pose.vy) * SHIP_RECONCILE_RATE;
        }
      }
    });
    for (const id of this.ships.keys()) {
      if (!seen.has(id)) this.ships.delete(id);
    }

    // Mount/dismount transitions: seed or drop the predicted ship. Only
    // the DRIVER predicts; a passenger rides the interpolated hull, and
    // predicting from their (ignored) input would fight the feed.
    const mountedId = this.self?.mounted ?? "";
    const flying = mountedId ? this.ships.get(mountedId) : undefined;
    if (!mountedId || !flying || flying.riderId !== this.selfId) {
      this.selfShip = undefined;
    } else if (this.selfShip?.id !== mountedId) {
      this.selfShip = { id: mountedId, pose: { ...flying.server }, serverPose: flying.server };
    }
  }

  private applySkiffs(stateSkiffs: SurfaceState["skiffs"] | undefined, now: number): void {
    const seen = new Set<string>();
    stateSkiffs?.forEach((skiff, id) => {
      seen.add(id);
      const server: SkiffPose = { x: skiff.x, z: skiff.z, yaw: skiff.yaw, speed: skiff.speed };
      let entry = this.skiffs.get(id);
      if (!entry) {
        entry = {
          buffer: new SnapshotBuffer(),
          renderPose: { x: skiff.x, z: skiff.z, yaw: skiff.yaw },
          server,
          speed: skiff.speed,
          riderId: skiff.riderId,
          passengerId: skiff.passengerId,
          plotted: skiff.plotted,
          wearBand: skiff.wearBand,
          chargeBand: skiff.chargeBand,
          ownerName: skiff.ownerName,
          locked: skiff.locked,
          town: skiff.town,
        };
        this.skiffs.set(id, entry);
      }
      entry.server = server;
      entry.speed = skiff.speed;
      entry.riderId = skiff.riderId;
      entry.passengerId = skiff.passengerId;
      entry.plotted = skiff.plotted;
      entry.wearBand = skiff.wearBand;
      entry.chargeBand = skiff.chargeBand;
      entry.ownerName = skiff.ownerName;
      entry.locked = skiff.locked;
      entry.town = skiff.town;
      entry.buffer.push({ t: now, x: skiff.x, z: skiff.z, yaw: skiff.yaw });

      // Reconcile the ridden skiff's prediction like the self pose.
      const mine = this.selfSkiff;
      if (mine?.id === id) {
        mine.serverPose = server;
        const dx = server.x - mine.pose.x;
        const dz = server.z - mine.pose.z;
        if (Math.hypot(dx, dz) > SKIFF_SNAP_DISTANCE) {
          mine.pose = { ...server };
        } else {
          mine.pose.x += dx * SKIFF_RECONCILE_RATE;
          mine.pose.z += dz * SKIFF_RECONCILE_RATE;
          mine.pose.yaw = lerpAngle(mine.pose.yaw, server.yaw, SKIFF_RECONCILE_RATE);
          mine.pose.speed += (server.speed - mine.pose.speed) * SKIFF_RECONCILE_RATE;
        }
      }
    });
    for (const id of this.skiffs.keys()) {
      if (!seen.has(id)) this.skiffs.delete(id);
    }

    // Mount/dismount transitions: seed or drop the predicted skiff. A
    // mount id that names a SHIP clears this too — one predicted ride.
    // Only the DRIVER predicts; a passenger rides the interpolated hull.
    const mountedId = this.self?.mounted ?? "";
    const ridden = mountedId ? this.skiffs.get(mountedId) : undefined;
    if (!mountedId || !ridden || ridden.riderId !== this.selfId) {
      this.selfSkiff = undefined;
    } else if (this.selfSkiff?.id !== mountedId) {
      this.selfSkiff = { id: mountedId, pose: { ...ridden.server }, serverPose: ridden.server };
    }
  }

  /** Mirror claims, structures and furniture; rebuild prediction obstacles. */
  private applyHomesteads(state: ClubState | SurfaceState | SpaceState | HomeState): void {
    let changed = false;

    const stateClaims = "claims" in state ? state.claims : undefined;
    const seenClaims = new Set<string>();
    stateClaims?.forEach((claim, id) => {
      seenClaims.add(id);
      const existing = this.claims.get(id);
      if (
        !existing ||
        existing.name !== claim.name ||
        existing.ownerName !== claim.ownerName ||
        existing.x !== claim.x ||
        existing.z !== claim.z
      ) {
        this.claims.set(id, {
          name: claim.name,
          ownerName: claim.ownerName,
          x: claim.x,
          z: claim.z,
        });
      }
    });
    for (const id of this.claims.keys()) {
      if (!seenClaims.has(id)) this.claims.delete(id);
    }

    const stateStructures = "structures" in state ? state.structures : undefined;
    const seenStructures = new Set<string>();
    stateStructures?.forEach((structure, id) => {
      seenStructures.add(id);
      const existing = this.structures.get(id);
      if (
        !existing ||
        existing.kind !== structure.kind ||
        existing.x !== structure.x ||
        existing.z !== structure.z ||
        existing.quarter !== structure.quarter ||
        existing.claimName !== structure.claimName ||
        existing.ownerName !== structure.ownerName ||
        existing.door !== structure.door
      ) {
        this.structures.set(id, {
          kind: (structure.kind === "shed" ? "shed" : "cabin") as StructureKind,
          x: structure.x,
          z: structure.z,
          quarter: asQuarter(structure.quarter),
          claimId: structure.claimId,
          claimName: structure.claimName,
          ownerName: structure.ownerName,
          door: structure.door,
        });
        changed = true;
      }
    });
    for (const id of this.structures.keys()) {
      if (!seenStructures.has(id)) {
        this.structures.delete(id);
        changed = true;
      }
    }

    const statePlaceables = "placeables" in state ? state.placeables : undefined;
    const seenPlaceables = new Set<string>();
    statePlaceables?.forEach((placeable, id) => {
      seenPlaceables.add(id);
      const existing = this.placeables.get(id);
      if (
        !existing ||
        existing.kind !== placeable.kind ||
        existing.cellX !== placeable.cellX ||
        existing.cellZ !== placeable.cellZ ||
        existing.rot !== placeable.rot ||
        existing.carried !== placeable.carried ||
        existing.makerName !== placeable.makerName
      ) {
        this.placeables.set(id, {
          kind: placeable.kind,
          cellX: placeable.cellX,
          cellZ: placeable.cellZ,
          rot: asQuarter(placeable.rot),
          carried: placeable.carried,
          makerName: placeable.makerName,
        });
        changed = true;
      }
    });
    for (const id of this.placeables.keys()) {
      if (!seenPlaceables.has(id)) {
        this.placeables.delete(id);
        changed = true;
      }
    }

    if ("label" in state) {
      this.homeLabel = state.label;
      this.homeOwnerName = state.ownerName;
      this.homeDoor = state.door;
    }

    if (changed) this.predictionPlan = undefined;
  }

  /**
   * The plan the prediction loop should integrate against — the room's
   * shared plan plus whatever players have raised or placed, exactly as
   * the server integrates it.
   */
  planForPrediction(): AreaPlan | undefined {
    if (!this.roomId) return undefined;
    if (this.predictionPlan?.id === this.roomId) return this.predictionPlan;
    const base = resolvePlan(this.roomId);
    const parsed = isHomeRoomId(this.roomId) ? parseHomeRoomId(this.roomId) : undefined;
    if (parsed) {
      const placed = [...this.placeables.values()];
      this.predictionPlan = {
        ...base,
        obstacles: placeableObstacles(parsed.kind, placed),
      };
    } else if (base.kind === "surface") {
      const shells = [...this.structures.values()].map((s) =>
        structureFootprint(s.kind, s.x, s.z, s.quarter),
      );
      this.predictionPlan = { ...base, obstacles: [...base.obstacles, ...shells] };
    } else {
      this.predictionPlan = base;
    }
    return this.predictionPlan;
  }

  /** Nearest stake or shell within reading reach, for the E prompt. */
  nearestHomestead(pose: PlayerPose, range: number): { id: string; label: string } | undefined {
    let best: { id: string; label: string } | undefined;
    let bestSq = range * range;
    for (const [id, claim] of this.claims) {
      const dx = claim.x - pose.x;
      const dz = claim.z - pose.z;
      const dSq = dx * dx + dz * dz;
      if (dSq <= bestSq) {
        best = { id, label: claim.name };
        bestSq = dSq;
      }
    }
    for (const [id, structure] of this.structures) {
      // The shell is big; read reach starts at its walls, not its centre.
      const slack = 5;
      const dx = structure.x - pose.x;
      const dz = structure.z - pose.z;
      const dSq = Math.max(0, Math.hypot(dx, dz) - slack) ** 2;
      if (dSq <= bestSq) {
        best = { id, label: structure.claimName };
        bestSq = dSq;
      }
    }
    return best;
  }

  /** Nearest piece of furniture within hand's reach (home rooms). */
  nearestPlaceable(pose: PlayerPose, range: number): string | undefined {
    const parsed = isHomeRoomId(this.roomId) ? parseHomeRoomId(this.roomId) : undefined;
    if (!parsed) return undefined;
    let best: string | undefined;
    let bestSq = range * range;
    for (const [id, placeable] of this.placeables) {
      if (placeable.carried) continue;
      const center = placeableCenter(parsed.kind, placeable);
      const dx = center.x - pose.x;
      const dz = center.z - pose.z;
      const dSq = dx * dx + dz * dz;
      if (dSq <= bestSq) {
        best = id;
        bestSq = dSq;
      }
    }
    return best;
  }

  /** The piece the local player is carrying, if any (owners only carry). */
  carriedPlaceable(): { id: string; kind: string } | undefined {
    if (!this.self || this.self.name !== this.homeOwnerName) return undefined;
    for (const [id, placeable] of this.placeables) {
      if (placeable.carried) return { id, kind: placeable.kind };
    }
    return undefined;
  }

  /** Sample every remote's render pose for the current frame. */
  sampleRemotes(now: number): void {
    const t = now - INTERP_DELAY_MS;
    for (const remote of this.remotes.values()) {
      const sampled = remote.buffer.sample(t);
      if (sampled) {
        remote.renderPose = { x: sampled.x, z: sampled.z, yaw: sampled.yaw };
      }
    }
    for (const [id, skiff] of this.skiffs) {
      if (this.selfSkiff?.id === id) {
        // Your own ride renders at the prediction, never the delayed feed.
        skiff.renderPose = {
          x: this.selfSkiff.pose.x,
          z: this.selfSkiff.pose.z,
          yaw: this.selfSkiff.pose.yaw,
        };
        continue;
      }
      const sampled = skiff.buffer.sample(t);
      if (sampled) {
        skiff.renderPose = { x: sampled.x, z: sampled.z, yaw: sampled.yaw };
      }
    }
    for (const [id, ship] of this.ships) {
      if (this.selfShip?.id === id) {
        // Your own ship renders at the prediction, never the delayed feed.
        ship.renderPose = {
          x: this.selfShip.pose.x,
          y: this.selfShip.pose.y,
          z: this.selfShip.pose.z,
          yaw: this.selfShip.pose.yaw,
        };
        continue;
      }
      const sampled = ship.buffer.sample(t);
      if (sampled) {
        ship.renderPose = { x: sampled.x, y: sampled.y ?? 0, z: sampled.z, yaw: sampled.yaw };
      }
    }
    for (const warden of this.wardens.values()) {
      const sampled = warden.buffer.sample(t);
      if (sampled) {
        warden.renderPose = { x: sampled.x, y: sampled.y ?? 0, z: sampled.z, yaw: sampled.yaw };
      }
    }
    for (const creature of this.creatures.values()) {
      const sampled = creature.buffer.sample(t);
      if (sampled) {
        creature.renderPose = { x: sampled.x, z: sampled.z, yaw: sampled.yaw };
      }
    }

    // A passenger's own body glues to the hull it is riding: no
    // prediction (their input moves nothing), just the interpolated seat.
    const self = this.self;
    if (self?.mounted && !this.selfSkiff && !this.selfShip) {
      const hull = this.skiffs.get(self.mounted) ?? this.ships.get(self.mounted);
      if (hull) {
        self.pose.x = hull.renderPose.x;
        self.pose.z = hull.renderPose.z;
        self.pose.yaw = hull.renderPose.yaw;
      }
    }
  }

  /**
   * Is anything alive close enough that a strike could mean it? Local
   * gate only — it keeps the key quiet when there is plainly nothing to
   * swing at; the server re-validates range and arc and its answer wins.
   */
  creatureInReach(pose: PlayerPose, range: number): boolean {
    for (const creature of this.creatures.values()) {
      if (creature.posture === "down") continue;
      const dx = creature.renderPose.x - pose.x;
      const dz = creature.renderPose.z - pose.z;
      if (dx * dx + dz * dz <= range * range) return true;
    }
    return false;
  }

  /** People close enough to hand something to, nearest first. */
  peopleWithinReach(): Array<{ sessionId: string; name: string }> {
    const self = this.self?.pose;
    if (!self) return [];
    return [...this.remotes.entries()]
      .map(([sessionId, remote]) => ({
        sessionId,
        name: remote.name,
        d: Math.hypot(remote.renderPose.x - self.x, remote.renderPose.z - self.z),
      }))
      .filter((entry) => entry.d <= TRADE_RANGE_M)
      .sort((a, b) => a.d - b.d)
      .map(({ sessionId, name }) => ({ sessionId, name }));
  }

  /** Nearest boardable (riderless) skiff within `range` metres, if any. */
  nearestSkiff(pose: PlayerPose, range: number): string | undefined {
    let best: string | undefined;
    let bestSq = range * range;
    for (const [id, skiff] of this.skiffs) {
      // A driven hull still offers its second seat; only a full one is
      // out of reach.
      if (skiff.riderId && skiff.passengerId) continue;
      const dx = skiff.renderPose.x - pose.x;
      const dz = skiff.renderPose.z - pose.z;
      const dSq = dx * dx + dz * dz;
      if (dSq <= bestSq) {
        best = id;
        bestSq = dSq;
      }
    }
    return best;
  }

  /** Nearest boardable (riderless, grounded) ship within `range` metres. */
  nearestShip(pose: PlayerPose, range: number): string | undefined {
    let best: string | undefined;
    let bestSq = range * range;
    for (const [id, ship] of this.ships) {
      // A flown ship set down still offers its second seat.
      if (ship.riderId && ship.passengerId) continue;
      const dx = ship.renderPose.x - pose.x;
      const dz = ship.renderPose.z - pose.z;
      const dSq = dx * dx + dz * dz;
      if (dSq <= bestSq) {
        best = id;
        bestSq = dSq;
      }
    }
    return best;
  }

  addChat(message: ChatBroadcastMessage): void {
    const entry: ChatEntry = { ...message, id: this.chatSeq++ };
    this.chat.push(entry);
    if (this.chat.length > CHAT_LOG_LIMIT) this.chat.shift();
    this.onChat?.(entry);
  }

  /** Reset volatile state before a (re)join; chat log survives. */
  resetForJoin(): void {
    this.selfId = undefined;
    this.self = undefined;
    this.selfSkiff = undefined;
    this.selfShip = undefined;
    this.remotes.clear();
    this.skiffs.clear();
    this.ships.clear();
    this.wardens.clear();
    this.creatures.clear();
    this.claims.clear();
    this.structures.clear();
    this.placeables.clear();
    this.homeLabel = "";
    this.homeOwnerName = "";
    this.homeDoor = "";
    this.predictionPlan = undefined;
  }

  private applySelf(
    name: string,
    appearance: string,
    serverPose: PlayerPose,
    mounted: string,
    carriage: Carriage,
  ): void {
    if (!this.self) {
      this.self = { name, appearance, pose: { ...serverPose }, serverPose, mounted, carriage };
      return;
    }
    this.self.appearance = appearance;
    this.self.serverPose = serverPose;
    this.self.mounted = mounted;
    this.self.carriage = carriage;
    // While mounted the pose mirrors the predicted skiff (reconciled via
    // the skiff itself); reconciling here too would double-correct.
    if (mounted) return;

    // Reconcile prediction: identical integrators keep the error near zero,
    // so a gentle pull suffices — snap only on real divergence.
    const pose = this.self.pose;
    const dx = serverPose.x - pose.x;
    const dz = serverPose.z - pose.z;
    if (Math.hypot(dx, dz) > SNAP_DISTANCE) {
      pose.x = serverPose.x;
      pose.z = serverPose.z;
      pose.yaw = serverPose.yaw;
    } else {
      pose.x += dx * RECONCILE_RATE;
      pose.z += dz * RECONCILE_RATE;
      pose.yaw = lerpAngle(pose.yaw, serverPose.yaw, RECONCILE_RATE);
    }
  }

  private applyRemote(
    sessionId: string,
    name: string,
    appearance: string,
    pose: PlayerPose,
    mounted: string,
    carriage: Carriage,
    now: number,
  ): void {
    let remote = this.remotes.get(sessionId);
    if (!remote) {
      remote = {
        name,
        appearance,
        buffer: new SnapshotBuffer(),
        renderPose: { ...pose },
        mounted,
        carriage,
      };
      this.remotes.set(sessionId, remote);
    }
    remote.name = name;
    remote.appearance = appearance;
    remote.mounted = mounted;
    remote.carriage = carriage;
    remote.buffer.push({ t: now, x: pose.x, z: pose.z, yaw: pose.yaw });
  }
}

/** World-space centre of a placed piece, for reach checks. */
function placeableCenter(
  structureKind: StructureKind,
  placeable: RemotePlaceable,
): { x: number; z: number } {
  if (!isPlaceableKind(placeable.kind)) return { x: 0, z: 0 };
  const box = placeableAabb(
    homeGrid(structureKind),
    placeable.kind,
    placeable.cellX,
    placeable.cellZ,
    placeable.rot,
  );
  return { x: (box.minX + box.maxX) / 2, z: (box.minZ + box.maxZ) / 2 };
}
